import { Lang } from './types'

export interface ReviewCopy {
  text: string
  meta: string
  service: string
  amount: string
}

export interface CourseCopy {
  name: string
  price: string
  priceNote?: string
  duration?: string
  includes: string[]
}

export interface LegalSection {
  heading: string
  body: string
}

export interface LegalDoc {
  title: string
  sections: LegalSection[]
}

export interface Copy {
  langName: string
  otherLangName: string
  switchLabel: string
  nav: { label: string; href: string }[]
  navAriaHome: string
  navOpenMenu: string
  navCloseMenu: string
  hero: {
    badge: string
    cta: string
    bannerAlt: string
  }
  intro: {
    eyebrow: string
    titleTop: string
    titleBottom: string
    subTop: string
    subBottom: string
    leadLine1: string
    leadLine2: string
    leadStrong: string
    leadLine3: string
    painEyebrow: string
    painQuestions1: string
    painQuestionsStrong: string
    painQuestions2: string
    painBody: string
    painClose: string
    promiseEyebrow: string
    promiseTitleTop: string
    promiseTitleBottom: string
    promiseBody1: string
    promiseBodyStrong: string
    promiseBody2: string
    promiseEm: string
    promiseBody3: string
  }
  gallery: {
    eyebrow: string
    title: string
    hint: string
    carouselLabel: string
    previous: string
    next: string
    selectLabel: string
    viewPrefix: string
    slides: { title: string; detail: string }[]
  }
  services: {
    rule: string
    eyebrow: string
    titleTop: string
    titleBottom: string
    body: string
  }
  academy: {
    eyebrow: string
    titleTop: string
    titleBottomPlain: string
    titleBottomEm: string
    body: string
    topics: string[]
    investment: string
    mostRequested: string
    cta: string
    note: string
    whatsappMessage: string
    courses: CourseCopy[]
  }
  reviews: {
    rule: string
    badge: string
    titleTop: string
    titleBottom: string
    items: ReviewCopy[]
  }
  location: {
    eyebrow: string
    openMap: string
    mapTitle: string
  }
  contact: {
    labelLocation: string
    labelHours: string
    labelWhatsApp: string
    labelCall: string
    hoursValueTop: string
    hoursValueBottom: string
    ctaEyebrow: string
    ctaTitleTop: string
    ctaTitleBottom: string
    ctaBody: string
    ctaButton: string
    writeUs: string
    orCall: string
    scheduleLine: string
  }
  booking: {
    eyebrow: string
    title: string
    close: string
    loading: string
    blocked: string
    openBooking: string
    footerPrefix: string
    whatsappLabel: string
    callLabel: string
    newTab: string
    dialogLabel: string
    iframeTitle: string
  }
  pay: {
    label: string
    aria: string
    menuTitle: string
    options: {
      service: { label: string; note: string }
      bridal: { label: string; note: string }
      academy: { label: string; note: string }
    }
  }
  footer: {
    logoAlt: string
    whatsappLabel: string
    callsLabel: string
    hours: string
    reviewsLine: string
    quote: string
    privacy: string
    terms: string
    effective: string
  }
  legal: {
    intro: string
    close: string
    effective: string
    privacy: LegalDoc
    terms: LegalDoc
  }
  opening: {
    aria: string
    alt: string
  }
  seo: {
    title: string
    description: string
    socialDescription: string
    twitterTitle: string
    twitterDescription: string
    keywords: string
    locale: string
    language: string
    htmlLang: string
  }
}

const es: Copy = {
  langName: 'Español',
  otherLangName: 'English',
  switchLabel: 'Cambiar idioma a inglés',
  nav: [
    { label: 'Inicio', href: '#inicio' },
    { label: 'Servicios', href: '#servicios' },
    { label: 'Novias', href: '#novias' },
    { label: 'Academia', href: '#academia' },
    { label: 'Reseñas', href: '#resenas' },
    { label: 'Contacto', href: '#contacto' },
  ],
  navAriaHome: 'Ginger Briones Studio inicio',
  navOpenMenu: 'Abrir menú',
  navCloseMenu: 'Cerrar menú',
  hero: {
    badge: 'Luxury Experience · 5.0 Google Reviews',
    cta: 'Reservar Mi Cita',
    bannerAlt: 'Ginger Briones Studio — salón de belleza de lujo en Queens, New York',
  },
  intro: {
    eyebrow: 'Para ti, mujer extraordinaria',
    titleTop: 'Mereces más',
    titleBottom: 'que un salón.',
    subTop: 'Mereces una',
    subBottom: 'experiencia.',
    leadLine1: 'Hay salones que te cortan el cabello.',
    leadLine2: 'Y hay lugares que ',
    leadStrong: 'transforman cómo te sientes contigo misma.',
    leadLine3: 'Ginger Briones Studio es lo segundo.',
    painEyebrow: 'Te entendemos',
    painQuestions1: '¿Cuántas veces saliste de un salón sintiéndote ',
    painQuestionsStrong: 'decepcionada',
    painQuestions2:
      '? ¿Pagaste más de lo que querías por un resultado que no fue lo que pediste? ¿Sentiste que eras solo otro turno más en la agenda?',
    painBody:
      'Sabemos que tu tiempo es limitado. Tu dinero, valioso. Y tu confianza en un estilista, algo que no se da fácilmente.',
    painClose: 'Eso es exactamente lo que cambia aquí.',
    promiseEyebrow: 'Ginger Briones Studio',
    promiseTitleTop: 'Cada visita es',
    promiseTitleBottom: 'una cita contigo misma.',
    promiseBody1:
      'Un espacio moderno, íntimo y elegante en el corazón de Queens donde Ginger — la dueña — te atiende personalmente, escucha lo que quieres y hace que tu visión se convierta en realidad. ',
    promiseBodyStrong: 'Sin prisa. Sin compromiso.',
    promiseBody2: ' Solo resultados que te hacen sentir ',
    promiseEm: 'espectacular',
    promiseBody3: '.',
  },
  gallery: {
    eyebrow: 'Galería de transformaciones',
    title: 'Resultados que hablan por sí solos.',
    hint: 'Usa flechas o teclado',
    carouselLabel: 'Galería de transformaciones de cabello',
    previous: 'Ver transformación anterior',
    next: 'Ver siguiente transformación',
    selectLabel: 'Seleccionar imagen de la galería',
    viewPrefix: 'Ver',
    slides: [
      { title: 'Ondas luminosas', detail: 'Dimensión cálida · acabado sedoso' },
      { title: 'Blonde dimension', detail: 'Color personalizado · brillo natural' },
      { title: 'Recogido editorial', detail: 'Textura pulida · detalle artesanal' },
      { title: 'Balayage miel', detail: 'Movimiento suave · luz a medida' },
      { title: 'Rubio sofisticado', detail: 'Contraste delicado · ondas definidas' },
      { title: 'Ondas caramelo', detail: 'Dimensión dorada · movimiento natural' },
      { title: 'Recogido de ocasión', detail: 'Trenza suave · detalle joya' },
      { title: 'Ponytail plata', detail: 'Tono frío · acabado pulido' },
      { title: 'Highlights luminosos', detail: 'Mechas finas · dimensión natural' },
      { title: 'Color completo', detail: 'Tono uniforme · brillo espejo' },
      { title: 'Balayage Ginger', detail: 'Iluminación a mano · efecto sol' },
      { title: 'Alisado espejo', detail: 'Liso perfecto · máximo brillo' },
      { title: 'Maquillaje full face', detail: 'Mirada definida · acabado impecable' },
      { title: 'Maquillaje glow up', detail: 'Piel luminosa · glow dorado' },
      { title: 'Money piece full face', detail: 'Luz al rostro · contraste marcado' },
    ],
  },
  services: {
    rule: 'Nuestros Servicios',
    eyebrow: 'Menú Completo de Servicios',
    titleTop: 'Todo lo que necesitas,',
    titleBottom: 'en un solo lugar.',
    body: 'Cada servicio es diseñado específicamente para ti, con productos de alta calidad y técnicas de vanguardia. Sin importar dónde te encuentres — si estás en Queens o llegas desde otra ciudad — tu experiencia aquí será impecable.',
  },
  academy: {
    eyebrow: 'Ginger Briones Studio — Academia',
    titleTop: '¿Quieres convertir',
    titleBottomPlain: 'tu pasión en ',
    titleBottomEm: 'profesión?',
    body: 'La academia de Ginger Briones Studio está diseñada para mujeres que quieren aprender de las mejores técnicas de la industria — con formación práctica, personalizada y con certificación profesional. Aprende desde cero o perfecciona lo que ya sabes.',
    topics: [
      'Curso de Maquillaje Profesional',
      'Curso de Peinados & Styling',
      'Colorimetría Avanzada',
      'Tratamientos Capilares',
    ],
    investment: 'Inversión',
    mostRequested: 'Más solicitado',
    cta: 'Consultar sobre los cursos',
    note: 'Cupos limitados por grupo. Precios sujetos a disponibilidad de fechas — escríbenos para reservar tu lugar.',
    whatsappMessage: 'Hola Ginger, quiero información sobre los cursos de la academia.',
    courses: [
      {
        name: 'Automaquillaje y Peinado',
        price: '$400',
        duration: '1 día — 5 horas',
        includes: ['Set de Brochas', 'Kit de Maquillaje', 'Brunch', 'Práctica en maniquís'],
      },
      {
        name: 'Colorimetría',
        price: '$800',
        priceNote: 'desde',
        includes: ['Programa personalizado', 'Certificación profesional'],
      },
      {
        name: 'Alisado',
        price: '$500',
        priceNote: 'desde',
        includes: ['Programa personalizado', 'Certificación profesional'],
      },
    ],
  },
  reviews: {
    rule: 'Lo que dicen ellas',
    badge: 'Reseñas Verificadas · Google',
    titleTop: 'Resultados reales.',
    titleBottom: 'Palabras reales.',
    items: [
      {
        text: '"Quería un cambio que se viera elegante, natural y con mucha luz, y superó por completo mis expectativas. El color quedó precioso, con una transición perfecta y un acabado súper profesional. La experiencia completa fue un 10/10. Desde que llegué me hicieron sentir muy cómoda. El ambiente del salón es hermoso, relajante y transmite mucha confianza."',
        meta: 'Mishell L. · Queens, NY',
        service: 'Balayage con ondas suaves',
        amount: '$200+',
      },
      {
        text: '"Hoy visité este salón y quedé encantada con la atención de la dueña Ginger. Me atendió personalmente, se preocupó por mi rubio y mi cabello quedó súper lacio. Ginger lo hizo genial — sin duda volvería por mi retoque de color."',
        meta: 'Claumile Q. · Corona, NY',
        service: 'Alisado · Tinturado',
        amount: '$200+',
      },
      {
        text: '"El servicio y la atención es de primera. Quería un estilo juvenil y quedé muy bien. La estilista Ginger Briones comprendió lo que quería y me dejó muy feliz."',
        meta: 'Jovanny G. · Queens, NY',
        service: 'Shampoo, Acondicionado & Corte',
        amount: '$20–40',
      },
    ],
  },
  location: {
    eyebrow: 'Visítanos',
    openMap: 'Abrir mapa',
    mapTitle: 'Mapa de Ginger Briones Studio en Corona, New York',
  },
  contact: {
    labelLocation: 'Ubicación',
    labelHours: 'Horario',
    labelWhatsApp: 'WhatsApp',
    labelCall: 'Llámanos',
    hoursValueTop: 'Lunes a domingo',
    hoursValueBottom: '9:30 AM – 8:00 PM',
    ctaEyebrow: 'Tu transformación comienza hoy',
    ctaTitleTop: 'Reserva tu cita y',
    ctaTitleBottom: 'siente la diferencia.',
    ctaBody:
      'No importa desde dónde llegues. Si buscas calidad premium, atención personalizada y salir del salón sintiéndote absolutamente espectacular — este es tu lugar. Cupos limitados por semana para garantizar una atención de primera.',
    ctaButton: 'Reservar Mi Cita Ahora',
    writeUs: 'Escríbenos por',
    orCall: 'o llámanos al',
    scheduleLine: 'Lunes a domingo · 9:30 AM – 8:00 PM · Queens, New York',
  },
  booking: {
    eyebrow: 'Reservas en línea',
    title: 'Elige tu servicio y horario',
    close: 'Cerrar reservas',
    loading: 'Cargando calendario…',
    blocked:
      'El calendario está tardando en cargar. Puedes abrir la página de reservas en una pestaña nueva.',
    openBooking: 'Abrir reservas',
    footerPrefix: '¿Prefieres hablar con nosotras?',
    whatsappLabel: 'WhatsApp (347) 888-6047',
    callLabel: 'Llamar (347) 242-2967',
    newTab: 'Abrir en pestaña nueva',
    dialogLabel: 'Reservar cita en línea',
    iframeTitle: 'Reserva de citas — Ginger Briones Studio',
  },
  pay: {
    label: 'Pagar',
    aria: 'Ver opciones de pago seguro con Square',
    menuTitle: 'Pago seguro · Square',
    options: {
      service: {
        label: 'Depósito de servicio',
        note: 'Se aplica al total de tu servicio',
      },
      bridal: {
        label: 'Depósito Reserva Novia',
        note: 'No reembolsable · se aplica al total',
      },
      academy: {
        label: 'Inscripción Academia',
        note: 'Se aplica al total del curso',
      },
    },
  },
  footer: {
    logoAlt: 'Ginger Briones Studio — Hair, Beauty, Wellness',
    whatsappLabel: 'WhatsApp',
    callsLabel: 'Llamadas',
    hours: 'Lunes a domingo · 9:30 AM – 8:00 PM',
    reviewsLine: '★★★★★ 5.0 Google Reviews · Queens, New York',
    quote: '“Porque cada mujer merece sentirse en su versión más hermosa.”',
    privacy: 'Privacidad',
    terms: 'Términos',
    effective: 'Vigente desde julio 2026',
  },
  legal: {
    intro:
      'Este aviso describe las políticas de Ginger Briones Studio, ubicado en 96-02 37th Ave, Corona, NY 11368.',
    close: 'Cerrar aviso legal',
    effective: 'Vigente desde julio 2026',
    privacy: {
      title: 'Política de Privacidad',
      sections: [
        {
          heading: 'Información que recopilamos',
          body: 'Cuando contactas a Ginger Briones Studio o solicitas una cita, podemos recopilar tu nombre, teléfono, correo electrónico, detalles de la cita y cualquier información que decidas compartir con nosotras.',
        },
        {
          heading: 'Cómo usamos la información',
          body: 'Usamos esta información para responder consultas, agendar y gestionar citas, prestar los servicios solicitados y comunicar información importante sobre el servicio.',
        },
        {
          heading: 'Protección y terceros',
          body: 'No vendemos información personal. Podemos apoyarnos en proveedores de confianza para operar nuestras comunicaciones, reservas y pagos. Tomamos medidas razonables para proteger la información que nos confías.',
        },
        {
          heading: 'Tus opciones',
          body: 'Puedes contactarnos para solicitar acceso, corrección o eliminación de la información personal que tenemos sobre ti, conforme a la normativa aplicable.',
        },
      ],
    },
    terms: {
      title: 'Términos del Servicio',
      sections: [
        {
          heading: 'Citas',
          body: 'Las citas están sujetas a disponibilidad y no se consideran confirmadas hasta que Ginger Briones Studio las acepte. Te pedimos llegar puntual para poder ofrecerte la mejor experiencia posible.',
        },
        {
          heading: 'Cancelaciones y cambios',
          body: 'Contáctanos lo antes posible si necesitas cancelar o reprogramar. Pueden aplicarse políticas de cancelación, retraso, depósito y ausencia, las cuales se comunican antes de reservar.',
        },
        {
          heading: 'Servicios y resultados',
          body: 'Las recomendaciones y los resultados son personalizados. El historial del cabello, su condición, el mantenimiento y el cuidado posterior influyen en el resultado. Podemos modificar o declinar un servicio cuando sea necesario por la salud o seguridad del cabello.',
        },
        {
          heading: 'Contacto',
          body: 'Para preguntas sobre estos términos o nuestros servicios, contacta a Ginger Briones Studio al (347) 242-2967.',
        },
      ],
    },
  },
  opening: {
    aria: 'Abriendo Ginger Briones Studio',
    alt: 'Ginger Briones Studio — Hair, Beauty, Wellness',
  },
  seo: {
    title: 'Ginger Briones Studio | Salón de Belleza de Lujo · Queens, New York',
    description:
      'Salón de belleza de lujo en Queens, NY. Balayage, mechas, color, keratina, alisados, maquillaje y bienestar. 96-02 37th Ave, Corona, NY. Reserva: (347) 242-2967',
    socialDescription:
      'Salón de belleza de lujo en Queens NY. Balayage, mechas, color y bienestar. 96-02 37th Ave, Corona, NY. Llama al (347) 242-2967',
    twitterTitle: 'Ginger Briones Studio | Queens, New York',
    twitterDescription:
      'Salón de belleza de lujo en Queens NY. Balayage, mechas, color, alisados y bienestar.',
    keywords:
      'salon de belleza Queens NY, peluqueria Corona NY, balayage Queens, mechas Queens NY, keratina Queens, alisado japones Queens, maquillaje profesional Queens, salon de belleza cerca de mi, Ginger Briones Studio, extensiones de cabello Queens, academia de belleza Queens, tratamientos capilares Corona NY',
    locale: 'es_US',
    language: 'Spanish',
    htmlLang: 'es',
  },
}

const en: Copy = {
  langName: 'English',
  otherLangName: 'Español',
  switchLabel: 'Switch language to Spanish',
  nav: [
    { label: 'Home', href: '#inicio' },
    { label: 'Services', href: '#servicios' },
    { label: 'Bridal', href: '#novias' },
    { label: 'Academy', href: '#academia' },
    { label: 'Reviews', href: '#resenas' },
    { label: 'Contact', href: '#contacto' },
  ],
  navAriaHome: 'Ginger Briones Studio home',
  navOpenMenu: 'Open menu',
  navCloseMenu: 'Close menu',
  hero: {
    badge: 'Luxury Experience · 5.0 Google Reviews',
    cta: 'Book My Appointment',
    bannerAlt: 'Ginger Briones Studio — luxury hair and beauty salon in Queens, New York',
  },
  intro: {
    eyebrow: 'For you, extraordinary woman',
    titleTop: 'You deserve more',
    titleBottom: 'than a salon.',
    subTop: 'You deserve an',
    subBottom: 'experience.',
    leadLine1: 'Some salons simply cut your hair.',
    leadLine2: 'And some places ',
    leadStrong: 'transform how you feel about yourself.',
    leadLine3: 'Ginger Briones Studio is the second kind.',
    painEyebrow: 'We understand you',
    painQuestions1: 'How many times have you left a salon feeling ',
    painQuestionsStrong: 'disappointed',
    painQuestions2:
      '? Paid more than you wanted for a result that was not what you asked for? Felt like just another slot on the schedule?',
    painBody:
      'We know your time is limited. Your money, valuable. And your trust in a stylist, something that is not given easily.',
    painClose: 'That is exactly what changes here.',
    promiseEyebrow: 'Ginger Briones Studio',
    promiseTitleTop: 'Every visit is',
    promiseTitleBottom: 'a date with yourself.',
    promiseBody1:
      'A modern, intimate and elegant space in the heart of Queens where Ginger — the owner — takes care of you personally, listens to what you want and turns your vision into reality. ',
    promiseBodyStrong: 'No rush. No pressure.',
    promiseBody2: ' Just results that make you feel ',
    promiseEm: 'spectacular',
    promiseBody3: '.',
  },
  gallery: {
    eyebrow: 'Transformation gallery',
    title: 'Results that speak for themselves.',
    hint: 'Use arrows or keyboard',
    carouselLabel: 'Hair transformation gallery',
    previous: 'View previous transformation',
    next: 'View next transformation',
    selectLabel: 'Select gallery image',
    viewPrefix: 'View',
    slides: [
      { title: 'Luminous waves', detail: 'Warm dimension · silky finish' },
      { title: 'Blonde dimension', detail: 'Custom color · natural shine' },
      { title: 'Editorial updo', detail: 'Polished texture · artisan detail' },
      { title: 'Honey balayage', detail: 'Soft movement · tailored light' },
      { title: 'Sophisticated blonde', detail: 'Delicate contrast · defined waves' },
      { title: 'Caramel waves', detail: 'Golden dimension · natural movement' },
      { title: 'Occasion updo', detail: 'Soft braid · jewel detail' },
      { title: 'Silver ponytail', detail: 'Cool tone · polished finish' },
      { title: 'Luminous highlights', detail: 'Fine strands · natural dimension' },
      { title: 'Full color', detail: 'Even tone · mirror shine' },
      { title: 'Ginger balayage', detail: 'Hand painted · sun-kissed effect' },
      { title: 'Mirror straightening', detail: 'Perfectly sleek · maximum shine' },
      { title: 'Full face makeup', detail: 'Defined eyes · flawless finish' },
      { title: 'Glow up makeup', detail: 'Luminous skin · golden glow' },
      { title: 'Money piece full face', detail: 'Face-framing light · bold contrast' },
    ],
  },
  services: {
    rule: 'Our Services',
    eyebrow: 'Complete Service Menu',
    titleTop: 'Everything you need,',
    titleBottom: 'in one place.',
    body: 'Every service is designed specifically for you, with high-quality products and cutting-edge techniques. Wherever you are coming from — here in Queens or from another city — your experience will be flawless.',
  },
  academy: {
    eyebrow: 'Ginger Briones Studio — Academy',
    titleTop: 'Want to turn',
    titleBottomPlain: 'your passion into a ',
    titleBottomEm: 'profession?',
    body: 'The Ginger Briones Studio academy is designed for women who want to learn the best techniques in the industry — with hands-on, personalized training and professional certification. Start from scratch or perfect what you already know.',
    topics: [
      'Professional Makeup Course',
      'Hairstyling & Styling Course',
      'Advanced Color Theory',
      'Hair Treatments',
    ],
    investment: 'Investment',
    mostRequested: 'Most requested',
    cta: 'Ask about the courses',
    note: 'Limited spots per group. Prices subject to date availability — message us to reserve your place.',
    whatsappMessage: 'Hi Ginger, I would like information about the academy courses.',
    courses: [
      {
        name: 'Self-Makeup & Hairstyling',
        price: '$400',
        duration: '1 day — 5 hours',
        includes: ['Brush set', 'Makeup kit', 'Brunch', 'Mannequin practice'],
      },
      {
        name: 'Color Theory',
        price: '$800',
        priceNote: 'from',
        includes: ['Personalized program', 'Professional certification'],
      },
      {
        name: 'Hair Straightening',
        price: '$500',
        priceNote: 'from',
        includes: ['Personalized program', 'Professional certification'],
      },
    ],
  },
  reviews: {
    rule: 'What they say',
    badge: 'Verified Reviews · Google',
    titleTop: 'Real results.',
    titleBottom: 'Real words.',
    items: [
      {
        text: '"I wanted a change that looked elegant, natural and full of light, and it completely exceeded my expectations. The color came out beautiful, with a perfect transition and a very professional finish. The whole experience was a 10/10. From the moment I arrived they made me feel comfortable. The salon is beautiful, relaxing and inspires a lot of confidence."',
        meta: 'Mishell L. · Queens, NY',
        service: 'Balayage with soft waves',
        amount: '$200+',
      },
      {
        text: '"I visited this salon today and I was delighted with the care from the owner, Ginger. She took care of me personally, looked after my blonde and my hair came out super sleek. Ginger did great — I would definitely come back for my color touch-up."',
        meta: 'Claumile Q. · Corona, NY',
        service: 'Straightening · Color',
        amount: '$200+',
      },
      {
        text: '"The service and the attention are first class. I wanted a youthful style and it turned out great. Stylist Ginger Briones understood exactly what I wanted and left me very happy."',
        meta: 'Jovanny G. · Queens, NY',
        service: 'Shampoo, Conditioning & Cut',
        amount: '$20–40',
      },
    ],
  },
  location: {
    eyebrow: 'Visit us',
    openMap: 'Open map',
    mapTitle: 'Map of Ginger Briones Studio in Corona, New York',
  },
  contact: {
    labelLocation: 'Location',
    labelHours: 'Hours',
    labelWhatsApp: 'WhatsApp',
    labelCall: 'Call us',
    hoursValueTop: 'Monday to Sunday',
    hoursValueBottom: '9:30 AM – 8:00 PM',
    ctaEyebrow: 'Your transformation starts today',
    ctaTitleTop: 'Book your appointment',
    ctaTitleBottom: 'and feel the difference.',
    ctaBody:
      'No matter where you are coming from. If you are looking for premium quality, personalized attention and walking out feeling absolutely spectacular — this is your place. Limited spots each week to guarantee first-class care.',
    ctaButton: 'Book My Appointment Now',
    writeUs: 'Message us on',
    orCall: 'or call us at',
    scheduleLine: 'Monday to Sunday · 9:30 AM – 8:00 PM · Queens, New York',
  },
  booking: {
    eyebrow: 'Online booking',
    title: 'Choose your service and time',
    close: 'Close booking',
    loading: 'Loading calendar…',
    blocked: 'The calendar is taking a while to load. You can open the booking page in a new tab.',
    openBooking: 'Open booking',
    footerPrefix: 'Prefer to talk to us?',
    whatsappLabel: 'WhatsApp (347) 888-6047',
    callLabel: 'Call (347) 242-2967',
    newTab: 'Open in new tab',
    dialogLabel: 'Book an appointment online',
    iframeTitle: 'Appointment booking — Ginger Briones Studio',
  },
  pay: {
    label: 'Pay',
    aria: 'View secure payment options with Square',
    menuTitle: 'Secure payment · Square',
    options: {
      service: {
        label: 'Service deposit',
        note: 'Applied to your service total',
      },
      bridal: {
        label: 'Bridal booking deposit',
        note: 'Non-refundable · applied to total',
      },
      academy: {
        label: 'Academy enrollment',
        note: 'Applied to the course total',
      },
    },
  },
  footer: {
    logoAlt: 'Ginger Briones Studio — Hair, Beauty, Wellness',
    whatsappLabel: 'WhatsApp',
    callsLabel: 'Calls',
    hours: 'Monday to Sunday · 9:30 AM – 8:00 PM',
    reviewsLine: '★★★★★ 5.0 Google Reviews · Queens, New York',
    quote: '“Because every woman deserves to feel like her most beautiful self.”',
    privacy: 'Privacy',
    terms: 'Terms',
    effective: 'Effective July 2026',
  },
  legal: {
    intro:
      'This notice describes the policies of Ginger Briones Studio, located at 96-02 37th Ave, Corona, NY 11368.',
    close: 'Close legal notice',
    effective: 'Effective July 2026',
    privacy: {
      title: 'Privacy Policy',
      sections: [
        {
          heading: 'Information we collect',
          body: 'When you contact Ginger Briones Studio or request an appointment, we may collect your name, phone number, email address, appointment details, and any information you choose to share with us.',
        },
        {
          heading: 'How we use information',
          body: 'We use this information to respond to inquiries, schedule and manage appointments, provide requested services, and communicate important service updates.',
        },
        {
          heading: 'Sharing and protection',
          body: 'We do not sell personal information. We may use trusted service providers to operate our communications, booking, and payment services. We take reasonable measures to protect the information entrusted to us.',
        },
        {
          heading: 'Your choices',
          body: 'You may contact us to request access to, correction of, or deletion of personal information we hold, subject to applicable legal requirements.',
        },
      ],
    },
    terms: {
      title: 'Terms of Service',
      sections: [
        {
          heading: 'Appointments',
          body: 'Appointments are subject to availability and are not confirmed until accepted by Ginger Briones Studio. Please arrive on time so we can provide the best possible experience.',
        },
        {
          heading: 'Cancellations and changes',
          body: 'Please contact us as early as possible if you need to cancel or reschedule. Cancellation, late-arrival, deposit, and no-show policies may apply and will be communicated before booking.',
        },
        {
          heading: 'Services and results',
          body: 'Recommendations and results are personalized to each guest. Hair history, condition, maintenance, and aftercare can affect outcomes. We may decline or modify a requested service when necessary for hair health or safety.',
        },
        {
          heading: 'Contact',
          body: 'For questions about these terms or our services, please contact Ginger Briones Studio at (347) 242-2967.',
        },
      ],
    },
  },
  opening: {
    aria: 'Opening Ginger Briones Studio',
    alt: 'Ginger Briones Studio — Hair, Beauty, Wellness',
  },
  seo: {
    title: 'Ginger Briones Studio | Luxury Hair Salon · Queens, New York',
    description:
      "Queens' premier luxury hair salon. Balayage, highlights, color, keratin, makeup and wellness treatments. 96-02 37th Ave, Corona, NY. Book now: (347) 242-2967",
    socialDescription:
      "Queens' premier luxury hair salon. Balayage, highlights, color and wellness. 96-02 37th Ave, Corona, NY. Call (347) 242-2967",
    twitterTitle: 'Ginger Briones Studio | Queens, New York',
    twitterDescription:
      'Luxury hair salon in Queens NY. Balayage, highlights, color and wellness.',
    keywords:
      'hair salon Queens NY, balayage Queens, highlights Corona NY, hair color Queens New York, luxury hair salon Queens, Ginger Briones Studio, hair salon Corona NY, best hair salon Queens, balayage near me Queens, keratin treatment Queens, hair extensions Queens NY, beauty academy Queens',
    locale: 'en_US',
    language: 'English',
    htmlLang: 'en',
  },
}

export const copy: Record<Lang, Copy> = { es, en }
